import { DeleteOutlined, PlusOutlined, LoginOutlined } from "@ant-design/icons";
import { defaultConfig, defaultConsumerConfig } from "Models/default-config";
import React from "react";
import Form from "antd/lib/form";
import InputNumber from "antd/lib/input-number";
import Select from "antd/lib/select";
import Slider from "antd/lib/slider";
import Button from "antd/lib/button";
import Divider from "antd/lib/divider";
import Row from "antd/lib/row";
import Typography from "antd/lib/typography";
import { FormLabelAlign } from "antd/lib/form/interface";
import { setConfiguration } from "Redux/actions";
import { Configuration as ConfigutrationModel } from "Models/configuration";
import { useDispatch } from "react-redux";
import { groupToLetter } from "./Visualization/utils";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const layout = {
    labelCol: { offset: 1, span: 8 },
    wrapperCol: { span: 14 },
    labelAlign: "left" as FormLabelAlign,
};

const generateMarks = (count: number) =>
    Object.fromEntries(
        Array.from({ length: Math.max(count, 1) }).map((_, k) => [
            k + 1,
            (k + 1).toString(),
        ])
    );
const marks = generateMarks(5);

export const Configuration: React.FC = () => {
    const [changed, setChanged] = React.useState(false);

    const dispatch = useDispatch();

    const onFinish = (values: any) => {
        setChanged(false);
        dispatch(setConfiguration(values as ConfigutrationModel));
    };

    const onValuesChange = () => {
        setChanged(true);
    };

    return (
        <Form
            className="config-form"
            name="kafka_config"
            {...layout}
            onFinish={onFinish}
            onValuesChange={onValuesChange}
            initialValues={defaultConfig}
        >
            <Divider>Configuration</Divider>

            <Paragraph>
                Choose the number of partitions - between which data will be
                evenly distributed. Experiment with various counts of brokers,
                turning them on and off, and seeing how the system adapts. Make
                sure to store data in replicas, so that they are not lost!
                Simulate load by increasing the consume interval. Finally,
                verify how offsets are commited, and see how this impacts
                redelivery when consumers or brokers are added/removed.
            </Paragraph>

            <Form.Item name="partitions" label="Partitions">
                <Slider min={1} max={5} marks={marks} />
            </Form.Item>

            <Form.Item label="Brokers" dependencies={["partitions"]}>
                {(form) => (
                    <Form.Item
                        name="nodes"
                        noStyle
                        rules={[
                            {
                                required: true,
                                message: "Brokers number required",
                            },
                        ]}
                    >
                        <Slider min={1} max={5} marks={marks} />
                    </Form.Item>
                )}
            </Form.Item>

            <Form.Item label="Replication factor" dependencies={["nodes"]}>
                {(form) => (
                    <Form.Item
                        name="replicas"
                        noStyle
                        rules={[
                            {
                                required: true,
                                message: "Replication factor required",
                            },
                            {
                                type: "number",
                                max: form.getFieldValue("nodes"),
                                message:
                                    "Replication factor cannot be larger than brokers",
                            },
                        ]}
                    >
                        <Slider min={1} max={5} marks={marks} />
                    </Form.Item>
                )}
            </Form.Item>
            <Divider>Producer</Divider>
            <Form.Item label="Producing interval">
                <Form.Item
                    name="producerInterval"
                    noStyle
                    rules={[
                        {
                            required: true,
                            message: "Producer speed required",
                        },
                    ]}
                >
                    <InputNumber min={1} max={5} />
                </Form.Item>
                <span className="ant-form-text">ticks</span>
            </Form.Item>

            <Divider>Consumers</Divider>

            <Form.List name="consumers">
                {(fields, { add, remove }, { errors }) => (
                    <>
                        {fields.map((field, i) => (
                            <React.Fragment key={i}>
                                <Row justify="space-between" align="top">
                                    <Title level={5}>Consumer {i + 1}</Title>
                                    {fields.length > 1 && (
                                        <Button
                                            type="dashed"
                                            onClick={() => remove(field.name)}
                                            icon={<DeleteOutlined />}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </Row>
                                <Form.Item label="Consume interval">
                                    <Form.Item
                                        noStyle
                                        name={[field.name, "consumerInterval"]}
                                        fieldKey={[
                                            field.fieldKey,
                                            "consumerInterval",
                                        ]}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Consumer speed required",
                                            },
                                        ]}
                                    >
                                        <InputNumber min={1} max={5} />
                                    </Form.Item>
                                    <span className="ant-form-text">ticks</span>
                                </Form.Item>

                                <Form.Item label="Commit offset interval">
                                    <Form.Item
                                        noStyle
                                        name={[field.name, "commitOffsetStep"]}
                                        fieldKey={[
                                            field.fieldKey,
                                            "commitOffsetStep",
                                        ]}
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    "Offset committing interval required",
                                            },
                                        ]}
                                    >
                                        <InputNumber min={1} max={10} />
                                    </Form.Item>
                                    <span className="ant-form-text">
                                        messages
                                    </span>
                                </Form.Item>

                                <Form.Item label="Consumer group">
                                    <Form.Item
                                        noStyle
                                        name={[field.name, "group"]}
                                        fieldKey={[field.fieldKey, "group"]}
                                    >
                                        <Select>
                                            {[0, 1, 2, 3, 4].map((group) => (
                                                <Option
                                                    key={group}
                                                    value={group.toString()}
                                                >
                                                    {groupToLetter(group)}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Form.Item>

                                <Divider dashed />
                            </React.Fragment>
                        ))}
                        {fields.length < 5 && (
                            <Row justify="end">
                                <Form.Item>
                                    <Button
                                        onClick={() =>
                                            add(defaultConsumerConfig)
                                        }
                                        icon={<PlusOutlined />}
                                    >
                                        Add Consumer
                                    </Button>
                                </Form.Item>
                            </Row>
                        )}
                        <Form.ErrorList errors={errors} />
                    </>
                )}
            </Form.List>
            <Row justify="center">
                <Form.Item>
                    <Button
                        type={changed ? "primary" : "default"}
                        htmlType="submit"
                        size="large"
                        icon={<LoginOutlined />}
                    >
                        Apply Setup
                    </Button>
                </Form.Item>
            </Row>
        </Form>
    );
};
